import { Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogContentText, FormControl, FormControlLabel, FormGroup, FormLabel, IconButton, InputAdornment, InputBase, makeStyles, MenuItem, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip } from "@material-ui/core";
import CheckIcon from '@material-ui/icons/Check';
import CloseIcon from '@material-ui/icons/Close';
import PencilIcon from '@material-ui/icons/Create';
import DeleteIcon from '@material-ui/icons/Delete';
import SearchIcon from '@material-ui/icons/Search';
import WarningIcon from '@material-ui/icons/Warning';
import clsx from "clsx";
import { cloneElement, forwardRef, memo, Suspense, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useRecoilState } from "recoil";
import { alertPosition, useAlert } from "../../hooks/useAlert";
import { Loading } from "../../parts/Loading";
import { AircraftDependencyType, aircraftListFilterWordState, useAircraftList, useAircraftListEdit, useLinkableSourceList } from "../../store/AircraftSettingState";
import { ReactComponent as ImageHeli } from '../../images/copilot/heli.svg';
import { ReactComponent as ImageCessna } from '../../images/copilot/cessna.svg';
import { ReactComponent as ImageNavi } from '../../images/copilot/navi.svg';
import RoleControl from "../RoleControl";
import { SUBJ_AIRCRAFT_SETTING_LIST, VERB_CREATE, VERB_DELETE, VERB_UPDATE } from "../../utils/AccessControl";
import { strSJISBytes } from "../../utils/FPEUtils";

const MAX_FILE_SIZE = 10 * 1024 * 1024;
const SUPPORT_IMAGE_TYPE = [
	'image/png',
	'image/jpeg',
	'image/gif',
	'image/webp',
];

const useStyles = makeStyles({
	container: {
		height: '100%',
		// border: '1px solid #a7a7a7',
	},
	stickyTable: {
		borderCollapse: 'separate',
		marginBottom: '1px',
	},
	fixedCell: {
		position: 'sticky',
		left: 0,
	},
	tableCell: {
		backgroundColor: 'white',
		borderRight: '1px solid #a7a7a7',
		borderBottom: '1px solid #a7a7a7',
		'th&': {
			borderTop: '1px solid #a7a7a7',
		},
		'&:nth-child(1)': {
			position: 'sticky',
			left: 0,
			zIndex: 2,
			borderLeft: '1px solid #a7a7a7',
		},
		'&:nth-child(2)': {
			position: 'sticky',
			left: '34px',
			zIndex: 2,
			// fontWeight: 'bold',
		},
	},
	headerCell: {
		color: '#fff',
		backgroundColor: '#4c4f53',
		whiteSpace: 'nowrap',
		position: 'sticky',
		zIndex: 2,
		top: 0,
		'&:nth-child(-n+2)': {
			zIndex: 3
		},
		'& a:link, a:visited': {
			color: '#fff',
		},
	},
	iconCell: {
		width: '32px',
		minWidth: '32px',
		maxWidth: '32px',
		verticalAlign: 'middle',
	},
	imageCell: {
		padding: '4px',
		width: '72px',
		boxSizing: 'border-box',
		'& img': {
			display: 'block',
			margin: '0 auto',
		}
	},
	iconBox: {
		width: 20,
		height: 20,
		display: 'flex',
		flexDirection: 'column',
		justifyContent: 'center',
	},
	center: {
		margin: '0 auto',
	},
	circle: {
		background: "#0000FF",
		border: "1px solid #0000FF",
		borderRadius: "50%",
		height: 7,
		width: 7,
		margin: "0 auto",
	},
	aircraftIcon: {
		fill: "#0000ff",
	},
	filterBox: {
		padding: '2px 4px',
		display: 'flex',
		alignItems: 'center',
		width: 400,
		marginBottom: 1,
		marginRight: 'auto',
	},
	input: {
		marginLeft: 8,
		flex: 1,
		"& .MuiInputBase-input": {
			padding: '3px 0 4px',
		}
	},
	iconButton: {
		padding: 1,
	},
	closeButton: {
		padding: 2,
	},
	searchIcon: {
		color: 'rgba(0, 0, 0, 0.54)',
	},
	warningIcon: {
		width: 20,
		height: 20,
		color: '#a7a7a7',
		verticalAlign: 'middle',
		cursor: 'pointer',
	},
	warningIconSmall: {
		width: 10,
		height: 10,
	},
	checkCell: {
		verticalAlign: 'middle',
		transition: 'background-color 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
	},
	checkIcon: {
		width: 24,
		height: 24,
		margin: '0 auto',
		'& .MuiSvgIcon-root': {
			color: 'rgb(2, 136, 209)',
		}
	},
	formInputText: {
		'& .MuiFormLabel-root': {
			color: 'black',
			'&.Mui-error': {
				color: '#f44336',
			}
		},
		'&:not(:first-child)': {
			marginTop: '20px',
		},
		'& .required': {
			color: '#f44336',
		}
	},
	dragImageBox: {
		border: '3px dashed rgba(128, 128, 128, 0.5)',
		borderRadius: '2px',
		'& *': {
			cursor: 'pointer',
		}
	},
	dragImageBoxLabel: {
		fontSize: '75%',
		margin: '-6px 0 2px 14px',
	},
	dragImageBoxActive: {
		borderColor: 'rgba(80, 80, 255, 0.8)',
	},
	dragImageTextBox: {
		width: '276px',
		height: '140px',
		display: 'flex',
		alignItems: 'center',
		boxSizing: 'border-box',
		padding: '10px',
		fontSize: '15px',
		whiteSpace: 'break-spaces',
	},
	dragImagePreview: {
		backgroundColor: '#bfbfbf',
		'& img': {
			width: '210px',
			height: '140px',
			backgroundColor: 'white',
			display: 'block',
			margin: '0 auto',
		}
	},
	externalSourceBox: {
		border: '1px solid rgba(0, 0, 0, 0.23)',
		borderRadius: 4,
		padding: '5px 10px',
		'&:hover': {
			borderColor: 'rgba(0, 0, 0, 0.87)',
		},
	},
	externalSourceBoxLabel: {
		fontSize: '75%',
		padding: '0 5px',
	},
	newline: {
		whiteSpace: 'break-spaces',
	},
});

const icons = [
	{ iconId: 0, icon: <div /> },
	{ iconId: 6, icon: <ImageHeli /> },
	{ iconId: 7, icon: <ImageCessna /> },
	{ iconId: 8, icon: <ImageNavi /> },
];

const columns = [
	{ id: 'aircraft_code', label: 'Aircraft Code' },
	{ id: 'aircraft_model', label: 'Model' },
	{ id: 'aircraft_name', label: 'Nickname' },
	{ id: 'aircraft_name_external', label: 'External Name' },
	{ id: 'aircraft_icon_no', label: 'Icon' },
	{ id: 'operation_site', label: 'Operating Base' },
	{ id: 'remarks', label: 'Remarks' },
	{ id: 'aircraft_serial_no', label: 'Serial Number' },
	{ id: 'aircraft_image', label: 'Image' },
];

const formInputList = [
	{
		code: 'aircraft_code',
		name: 'Aircraft Code',
		type: 'text',
		rules: {
			required: true,
			pattern: /^[0-9a-zA-Z-]{1,20}$/,
			maxLength: 20,
			unique: true,
			reedit: false,
		},
	},
	{
		code: 'aircraft_model',
		name: 'Model',
		type: 'text',
		rules: {
			required: false,
			pattern: /^[0-9a-zA-Z-]{0,20}$/,
			maxLength: 20,
		},
	},
	{
		code: 'aircraft_name',
		name: 'Nickname',
		type: 'text',
		rules: {
			required: true,
			pattern: /^[0-9a-zA-Z-]{1,20}$/,
			maxLength: 20,
		},
	},
	{
		code: 'aircraft_name_external',
		name: 'External Name',
		type: 'text',
		rules: {
			required: true,
			pattern: /^[^'"!@#$%^&*();:{}[\]>?]{1,20}$/,
			maxLength: 20,
		},
	},
	{
		code: 'aircraft_icon_no',
		name: 'Icon',
		type: 'select',
		rules: {
			required: true,
		},
	},
	{
		code: 'operation_site',
		name: 'Operating Base',
		type: 'text',
		rules: {
			required: false,
			pattern: /^[^'"!@#$%^&*();:{}[\]>?]{0,10}$/,
			maxLength: 10,
		},
	},
	{
		code: 'remarks',
		name: 'Remarks',
		type: 'text',
		compressSpace: true,
		rules: {
			required: false,
			pattern: /^[^'"!@#$%^&*();:{}[\]>?]{0,140}$/,
			maxLength: 140,
			maxByteLength: 140,
		},
	},
	{
		code: 'aircraft_serial_no',
		name: 'Serial Number',
		type: 'text',
		rules: {
			required: false,
			pattern: /^[0-9a-zA-Z-]{0,20}$/,
			maxLength: 20,
		},
	},
];

const formDefault = formInputList.reduce((a, v) => {
	if (v.type === 'select') {
		a[v.code] = 0;
	} else {
		a[v.code] = "";
	}
	return a;
}, {});


const DependenciesState = () => {
	useAircraftListEdit();
	return null;
};

const SuspendableInput = ({children, deps, ...props}) => {
	return (
		<Suspense fallback={cloneElement(children, {...props, disabled: true})}>
			{deps}
			{cloneElement(children, {...props})}
		</Suspense>
	);
};

const AircraftListTable = memo(({filterWord, handleEdit, dialogRef, tableRef, setWaiting}) => {
	const {
		aircraftList,
		checkDependency,
		refreshAircraftList
	} = useAircraftList();
	const {
		linkableSourceList
	} = useLinkableSourceList(true);
	const [del, setDel] = useState();
	const { showError } = useAlert();

	const classes = useStyles();
	const {t} = useTranslation();

	const handleDelete = (row) => {
		const data = {
			column_name: 'aircraft_code',
			value: row.aircraft_code,
		};

		setWaiting(true);
		checkDependency(data)
			.then(res => {
				if (res.size === 0) {
					setDel(row);
					setOpen(true);
				} else if (res.size === 1 && res.has(AircraftDependencyType.LINKAGE_AIRCRAFT_EXTERNAL_SOURCE)) {
					setDel(row);
					setOpen(true);
				} else {
					if (res.has(AircraftDependencyType.LINKAGE_AIRCRAFT_AIRCRAFTGROUP)) {
						setMessageAlert(t('AIRCRAFTGROUP_LINKED_WARNING'));
					} else if (res.has(AircraftDependencyType.LINKAGE_AIRCRAFT_COPILOT)) {
						setMessageAlert(t('COPILOT_LINKED_WARNING'));
					} else if (res.has(AircraftDependencyType.LINKAGE_AIRCRAFT_FOSTERGA)) {
						setMessageAlert(t('FOSTERGA_LINKED_WARNING'));
					} else if (res.has(AircraftDependencyType.LINKAGE_OTHER)) {
						setMessageAlert(t('AIRCRAFT_OTHER_LINKED_WARNING'));
					}
					setOpenAlert(true);
				}
			})
			.catch(e => {
				showError(t('DATA_LOAD_ERROR'));
			})
			.finally(() => {
				setWaiting(false);
			});
	}

	const list = useCallback(() => {
		if (filterWord === "") {
			return aircraftList;
		}
		return aircraftList.filter(row => {
			return columns.some(col => {
				switch (col.id) {
					default:
						const cell = row[col.id] && ((col.format && col.format(row[col.id])) || row[col.id]);
						return cell !== undefined && cell !== null && String(cell).indexOf(filterWord) !== -1;
				}
			});
		});
	}, [aircraftList, filterWord]);

	useEffect(() => {
		return () => {
			refreshAircraftList();
		}
	}, [refreshAircraftList]);

	// 確認ダイアログ
	const [open, setOpen] = useState(false);

	const handleDeleteOk = () =>{
		// const _data = {
		// 	aircraft_code: del.aircraft_code
		// };
		setOpen(false);
		dialogRef.current.handleDelete(del);
	};

	const handleClose = () => {
		setOpen(false);
	};

	const handleKeyUp = e => {
		if (e.keyCode === 13) {
			handleDeleteOk();
		}
	};

	const [openAlert, setOpenAlert] = useState(false);
	const handleCloseAlert = () => {
		setOpenAlert(false);
	};
	const [messageAlert, setMessageAlert] = useState('');

	let detail = '';
	if (del) {
		detail = [
			"\n",
			`${t('Aircraft Code')}: ${del.aircraft_code}`
		].join('\n');
	}

	return (
		<TableContainer className={classes.container} ref={tableRef}>
			<Table aria-label="sticky table" size="small" className={classes.stickyTable}>
				<TableHead>
					<TableRow className={classes.tableRow}>
						<TableCell
							key='edit'
							className={clsx(classes.headerCell, classes.tableCell, classes.iconCell)}
							padding="none"
						>
						</TableCell>
						{columns.map((column, i) => (
							<TableCell
								key={column.id}
								align={column.align}
								style={{ minWidth: 0 }}
								className={clsx(classes.headerCell, classes.tableCell)}
							>
								{t(column.label)}
								{column.id === 'aircraft_name_external' &&
									<Tooltip title={t('AIRCRAFT_NAME_EXTERNAL_DESCRIBE')}>
										<WarningIcon className={clsx(classes.warningIcon)} viewBox="0 0 24 24" alt="Describe"/>
									</Tooltip>
								}
							</TableCell>
						))}
						{linkableSourceList.map((column, i) => (
							<TableCell
								key={column.data_source_id}
								style={{ minWidth: 0 }}
								className={clsx(classes.headerCell, classes.tableCell)}
							>
								{t(column.data_source_name)}
							</TableCell>
						))}
						<TableCell
							key='delete'
							className={clsx(classes.headerCell, classes.tableCell, classes.iconCell)}
							padding="none"
						>
						</TableCell>
					</TableRow>
				</TableHead>
				<TableBody>
					{list().map((row) => {
						return (
							<TableRow hover role="checkbox" tabIndex={-1} key={row.aircraft_code}>
								<TableCell key='editBtn' className={classes.tableCell} padding="none" align="center">
									<RoleControl subject={SUBJ_AIRCRAFT_SETTING_LIST} verb={VERB_UPDATE}>
										<IconButton aria-label="edit" size="small" className={classes.smallBtn} onClick={() => handleEdit(row)}>
											<Tooltip title="edit" arrow >
												<PencilIcon fontSize="small" />
											</Tooltip>
										</IconButton>
									</RoleControl>
								</TableCell>
								{columns.map((column, i) => {
									const value = row[column.id];
									return (
										<TableCell
											key={column.id}
											align={column.align}
											className={clsx(classes.tableCell, column.id === 'aircraft_image' && classes.imageCell)}
										>
											{
												column.id === 'aircraft_icon_no' ?
													<div className={clsx(classes.iconBox, classes.center)}>
														{![6, 7, 8].includes(value) && <div className={classes.circle}/>}
														{value === 6 && <ImageHeli className={classes.aircraftIcon}/>}
														{value === 7 && <ImageCessna className={classes.aircraftIcon}/>}
														{value === 8 && <ImageNavi className={classes.aircraftIcon}/>}
													</div> :
												column.id === 'aircraft_image' ?
													row['aircraft_image'] !== null && <img src={row['aircraft_image']} style={{width: '72px', height: '48px'}} alt={row['aircraft_code']} /> :
													column.format && typeof value === 'number' ? column.format(value) : value
											}
										</TableCell>
									);
								})}
								{linkableSourceList.map((column, i) => (
									<TableCell
										key={`cell-${row.aircraft_code}-${column.data_source_id}`}
										align="center"
										padding="checkbox"
										style={{ minWidth: 0 }}
										className={clsx(classes.tableCell, classes.checkCell)}
									>
										{row?.linked_data_source_id?.includes(column.data_source_id) &&
											<Box className={clsx(classes.checkIcon)}>
												<CheckIcon color="secondary" />
											</Box>
										}
									</TableCell>
								))}
								<TableCell key='deleteBtn' className={classes.tableCell} padding="none" align="center">
									<RoleControl subject={SUBJ_AIRCRAFT_SETTING_LIST} verb={VERB_DELETE}>
										<IconButton aria-label="delete" size="small" className={classes.smallBtn} onClick={() => handleDelete(row)}>
											<Tooltip title="delete" arrow >
												<DeleteIcon fontSize="small" />
											</Tooltip>
										</IconButton>
									</RoleControl>
								</TableCell>
							</TableRow>
						);
					})}
					<Dialog open={open} onClose={handleClose} onKeyUp={handleKeyUp}>
						<DialogContent>
							<DialogContentText className={classes.newline}>{t('AIRCRAFT_DELETE_CONFIRM', {detail})}</DialogContentText>
						</DialogContent>
						<DialogActions>
							<Button onClick={handleClose} color="primary">Cancel</Button>
							<Button onClick={handleDeleteOk} color="primary">Ok</Button>
						</DialogActions>
					</Dialog>
					<Dialog open={openAlert} onClose={handleCloseAlert}>
						<DialogContent>
							<DialogContentText className={classes.newline}>{messageAlert}</DialogContentText>
						</DialogContent>
						<DialogActions>
							<Button onClick={handleCloseAlert} color="primary">OK</Button>
						</DialogActions>
					</Dialog>
				</TableBody>
			</Table>
		</TableContainer>
	);
});

function AircraftList() {
	const tableRef = useRef(null);
	const dialogRef = useRef({});
	const inputFilterRef = useRef(null);
	const [filterWord, setFileterWord] = useRecoilState(aircraftListFilterWordState);
	const [open, setOpen] = useState(false);
	const [waiting, setWaiting] = useState(false);

	const classes = useStyles();
	const {t} = useTranslation();

	const cancelDrop = event => {
		event.preventDefault();
		return false;
	}

	useEffect(() => {
		document.addEventListener('dragover', cancelDrop, false);
		document.addEventListener('drop', cancelDrop, false);
		return () => {
			document.removeEventListener('dragover', cancelDrop, false);
			document.removeEventListener('drop', cancelDrop, false);
			setFileterWord('');
		}
	}, [setFileterWord]);

	const handleNew = () => {
		dialogRef.current.handleNew();
		setOpen(true);
	}

	const handleEdit = (row) => {
		dialogRef.current.handleEdit(row);
		setOpen(true);
	}

	const handleSearch = () => {
		const word = inputFilterRef.current.value;
		setFileterWord(word);
		tableRef.current.scrollTop = 0;
	}

	const handleDeleteFilter = () => {
		const word = "";
		inputFilterRef.current.value = word;
		setFileterWord(word);
		tableRef.current.scrollTop = 0;
	}

	return (
		<Box style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
			<Box style={{ display: 'flex', justifyContent: 'flex-end', padding: '10px 50px 10px 10px' }}>
				<Paper component="form" className={classes.filterBox} onSubmit={(event) => { handleSearch(); event.preventDefault(); }}>
					<div className={classes.searchIcon}>
						<SearchIcon />
					</div>
					<InputBase
						className={classes.input}
						placeholder={`${t('Aircraft Code')}、${t('Model')}、${t('Nickname')} ...`}
						inputProps={{ 'aria-label': 'filter aircraft list' }}
						inputRef={inputFilterRef}
						defaultValue={filterWord}
						// onChange={handleChange}
					/>
					{filterWord.length > 0 && <IconButton type="button" className={classes.iconButton} aria-label="delete" onClick={handleDeleteFilter}>
						<CloseIcon />
					</IconButton>}
				</Paper>
				<RoleControl subject={SUBJ_AIRCRAFT_SETTING_LIST} verb={VERB_CREATE}
					ok={
						<SuspendableInput deps={<DependenciesState />}>
							<Button variant="outlined" size="small" className={classes.btn} onClick={handleNew} style={{ height: '100%' }}>{t('New')}</Button>
						</SuspendableInput>
					}
					ng={
						<Button variant="outlined" size="small" className={classes.btn} disabled style={{ height: '100%' }}>{t('New')}</Button>
					}
				/>
			</Box>
			<Box style={{ padding: '0 10px 10px', flexGrow: 1, overflow: 'hidden' }}>
				{waiting && <Loading backdrop={true} />}
				<Suspense fallback={<Loading />}>
					<AircraftListTable filterWord={filterWord} handleEdit={handleEdit} dialogRef={dialogRef} tableRef={tableRef} setWaiting={setWaiting} />
				</Suspense>
			</Box>
			
			<RoleControl subject={SUBJ_AIRCRAFT_SETTING_LIST} verb={VERB_CREATE}
				ok={
					<Suspense>
						<AircraftForm open={open} setOpen={setOpen} setWaiting={setWaiting} ref={dialogRef} />
					</Suspense>
				}
			/>
		</Box>
	)
}

const AircraftForm = memo(forwardRef(({ open, setOpen, setWaiting }, ref) => {
	const {
		aircraftList,
		linkableSourceDetailList,
		registerAircraft,
		updateAircraft,
		deleteAircraft,
		refreshAircraftList
	} = useAircraftListEdit();

	const [formData, setFormData] = useState({});
	const [onTarget, setOnTarget] = useState(false);
	const [preview, setPreview] = useState();
	const [editMode, setEditMode] = useState(false);
	const [imageChanged, setImageChanged] = useState(false);
	const imageRef = useRef();

	const { control, formState: { errors, isDirty, isValid }, getValues, reset, watch } = useForm({
		mode: 'onChange',
	});
	const { showError, showWarning } = useAlert();

	const classes = useStyles();
	const {t} = useTranslation();

	const linkableAircraftList = useMemo(() => {
		return linkableSourceDetailList.reduce((a, v) => {
			v.aircraft_code_list.forEach(aircraftCode => {
				if (!a[aircraftCode]) {
					a[aircraftCode] = {};
				}
				a[aircraftCode][v.data_source_id] = true;
			});
			return a;
		}, {});
	}, [linkableSourceDetailList]);

	const watchAircraftCode = watch('aircraft_code');
	const isDisabled = useCallback((source) => {
		const aircraftCode = watchAircraftCode;
		return !linkableAircraftList[aircraftCode]?.[source.data_source_id];
	}, [watchAircraftCode, linkableAircraftList]);

	const handleClose = () => {
		setOpen(false);
	}

	const handleNew = () => {
		const _formDefault = {
			...formDefault,
			linked_data_source: Object.fromEntries(linkableSourceDetailList.map(v => [v.data_source_id, false])),
		}
		setEditMode(false);
		reset(_formDefault);
		setFormData({});
		setPreview();
		setImageChanged(false);
	}

	const handleEdit = (row) => {
		setEditMode(true);
		const _formData = Object.assign({}, row);

		_formData.linked_data_source = Object.fromEntries(linkableSourceDetailList.map(v => {
			return [v.data_source_id, !!row.linked_data_source_id?.includes(v.data_source_id)];
		}));

		Object.keys(formDefault).forEach(key => _formData[key] === null && (_formData[key] = formDefault[key]));
		reset(_formData);
		setFormData(row);
		setPreview();
		setImageChanged(false);
	}

	const handleDelete = (row) => {
		const _data = Object.assign({}, row);
		if (_data.aircraft_image) {
			_data.image_aircraft_code = _data.aircraft_code;
		}
		processDelete(_data);
	}

	const handleSave = () => {
		setOpenConfirm(true);
	}

	const handleSaveOk = () => {
		const data = formInputList.reduce((a, v) => {
			const code = v.code;
			const value = getValues(code);
			if (value !== "") {
				a[code] = value;
			} else {
				a[code] = null;
			}
			return a;
		}, {})

		//Remarksの補正
		if (data['remarks']) {
			data['remarks'] = data['remarks'].replace(/　/g, ' ').replace(/ +/g, ' ');
		}

		//外部ソース
		const linkedDataSource = getValues('linked_data_source');
		if (linkedDataSource) {
			data['linked_data_source_id'] = Object.entries(linkedDataSource)
				.filter(([sourceId, v]) => {
					const sources = linkableAircraftList[data['aircraft_code']];
					return sources && sources[sourceId] && v;
				})
				.map(([sourceId]) => sourceId);
		}

		const promise = new Promise((resolve) => {
			if (imageChanged && imageRef.current) {
				const img = imageRef.current;
				const canvas = document.createElement('canvas');
				canvas.width = 300;
				canvas.height = 200;
				const ctx = canvas.getContext('2d');
				ctx.drawImage(img, 0, 0, img.naturalWidth, img.naturalHeight, 0, 0, 300, 200);
				canvas.toBlob(blob => {
					resolve(blob);
				}, 'image/png');
			} else {
				resolve();
			}
		});

		promise.then(blob => {
			if (!editMode) {
				if (blob) {
					data.image_aircraft_code = data.aircraft_code;
					data.aircraft_image_blob = blob;
				}
				// if (!('aircraft_name' in data)) {
				if (data.aircraft_name === null) {
					data.aircraft_name = data.aircraft_code;
				}
				processRegister(data);
			} else {
				const diff = Object.keys(data).reduce((previousValue, currentValue) => {
					if (formData[currentValue] !== data[currentValue]) {
						previousValue[currentValue] = data[currentValue];
					}
					return previousValue;
				}, {});

				let _data = null;
				if (Object.keys(diff).length > 0) {
					_data = Object.assign({}, formData, diff);
				}

				const lockObj = {
					aircraft_code: formData.aircraft_code,
					update_time: formData.update_time,
					update_by_user: formData.update_by_user,
				};

				let imageData;
				if (!imageChanged) {
					//画像変更なし
					imageData = null;
				} else if (formData.aircraft_image && blob) {
					//画像変更
					imageData = {
						delete: {
							image_aircraft_code: formData.aircraft_code,
						},
						register: {
							image_aircraft_code: formData.aircraft_code,
							aircraft_image_blob: blob,
						},
					};
				} else if (formData.aircraft_image && !blob) {
					//画像削除
					imageData = {
						delete: {
							image_aircraft_code: formData.aircraft_code,
						},
					};
				} else if (!formData.aircraft_image && blob) {
					//画像追加
					imageData = {
						register: {
							image_aircraft_code: formData.aircraft_code,
							aircraft_image_blob: blob,
						},
					};
				}
				processUpdate(lockObj, _data, imageData);
			}
		});
		setOpenConfirm(false);
		setOpen(false);
	}

	const processRegister = (registerData) => {
		setWaiting(true);
		registerAircraft(registerData)
			.then(() => {
				// console.log("register comp");
			})
			.catch(e => {
				console.error("register error", e);
				if (e?.response?.status === 409) {
					showWarning(t('validate_duplicate'));
					refreshAircraftList();
				} else if (e?.response?.status === 428) {
					showWarning(t('validate_optimistic_lock'));
					refreshAircraftList();
				} else {
					showError(`${t('Register failed')}`);
				}
			})
			.finally(() => {
				setWaiting(false);
			});
	};

	const processUpdate = (lockObj, updateData, updateImageData) => {
		setWaiting(true);
		updateAircraft(lockObj, updateData, updateImageData)
			.then(() => {
				// console.log("update comp");
			})
			.catch(e => {
				console.error("update error", e);
				if (e?.response?.status === 428) {
					showWarning(t('validate_optimistic_lock'));
					refreshAircraftList();
				} else {
					showError(`${t('Update failed')}`);
				}
			})
			.finally(() => {
				setWaiting(false);
			});
	};

	const processDelete = (deleteData) => {
		setWaiting(true);
		deleteAircraft(deleteData)
			.then(() => {
				// console.info("delete comp");
			})
			.catch(e => {
				console.error("delete error", e);
				if (e?.response?.status === 428) {
					showWarning(t('validate_optimistic_lock'));
					refreshAircraftList();
				} else if (e?.response?.status === 490) {
					showError(t('AIRCRAFT_EXTERNAL_LINKED_WARNING'));
				} else if (e?.response?.status === 424) {
					showError(t('AIRCRAFTGROUP_OTHER_LINKED_WARNING'));
				} else {
					showError(`${t('Delete failed')}`);
				}
			})
			.finally(() => {
				setWaiting(false);
			});
	};

	// 画像
	const changePreview = file => {
		if (file) {
			if (file.size > MAX_FILE_SIZE) {
				showError(t('File size is too large'), { position: alertPosition.BOTTOM_CENTER, timeout: 5000 });
				return;
			}
			if (!SUPPORT_IMAGE_TYPE.includes(file.type)) {
				showError(t('Unsupported file type'), { position: alertPosition.BOTTOM_CENTER, timeout: 5000 });
				return;
			}
			const fileReader = new FileReader();
			fileReader.onload = (event) => {
				setImageChanged(true);
				setPreview(event.target.result);
			}
			fileReader.readAsDataURL(file);
		} else {
			setPreview();
		}
	}

	const handleImageChange = event => {
		const files = event.target.files;
		if (files.length >= 1) {
			changePreview(files[0]);
		}
	}

	const handleDragOver = event => {
		event.preventDefault();
		setOnTarget(true);
	}

	const handleDragLeave = event => {
		event.preventDefault();
		setOnTarget(false);
	}

	const handleDrop = event => {
		event.preventDefault();
		setOnTarget(false);
		const files = event.dataTransfer.files;
		if (files.length >= 1) {
			changePreview(files[0]);
		}
	}

	// 確認ダイアログ
	const [openConfirm, setOpenConfirm] = useState(false);

	const handleCloseConfirm = () => {
		setOpenConfirm(false);
	};

	const handleKeyUpConfirm = e => {
		if (e.keyCode === 13) {
			handleSaveOk();
		}
	};

	useImperativeHandle(ref, () => ({
		handleNew,
		handleEdit,
		handleDelete,
	}));

	return (
		<Box>
			<Dialog onClose={handleClose} aria-labelledby="customized-dialog-title" open={open}>
				<DialogContent>
					<Box style={{ display: 'flex', justifyContent: 'flex-end', flexDirection: 'row-reverse' }}>
						<Box style={{ display: 'flex', justifyContent: 'space-between', flexDirection: 'column', marginLeft: '15px' }}>
							<Box style={{ marginLeft: 'auto' }}>
								<IconButton aria-label="close" className={classes.closeButton} onClick={handleClose}>
									<CloseIcon />
								</IconButton>
							</Box>
							<Box>
								<Button variant="outlined" size="small" className={classes.btn} onClick={handleSave} disabled={!(isDirty && isValid) && !(isValid && imageChanged)}>{t('save')}</Button>
							</Box>
						</Box>
						<Box style={{ display: 'flex', flexDirection: 'column', paddingBottom: '40px', minWidth: '230px' }}>
							{formInputList.map(item => (
								<Controller
									key={item.code}
									control={control}
									rules={{
										required: item.rules.required ? t('validate_required') : false,
										// pattern: {
										// 	value: item.rules.pattern,
										// 	message: t('validate_pattern')
										// },
										// maxLength: {
										// 	value: item.rules.maxLength,
										// 	message: t('validate_max_length', { maxLength: item.rules.maxLength }),
										// },
										validate: value => {
											const _value = !item.compressSpace ? value : value.replace(/　/g, ' ').replace(/\s+/g, " ");
											//文字数チェック
											if (item.rules.maxLength && _value.length > item.rules.maxLength) {
												return t('validate_max_length', { maxLength: item.rules.maxLength });
											}
											if (item.rules.maxByteLength && strSJISBytes(_value) > item.rules.maxByteLength) {
												return t('Too many characters');
											}
											//パターンチェック
											if (item.rules.pattern && !item.rules.pattern.test(_value)) {
												return t('validate_pattern');
											}
											//一意チェック
											if (!item.rules.unique) {
												return true;
											}
											if (formData[item.code] === value) {
												return true;
											}
											const check = (item, value) => {
												return aircraftList.every(v => v[item] !== value);
											}
											// return childRef.current.check(item.code, value) || t('already exists')
											return check(item.code, value) || t('already exists');
										}
									}}
									render={({ field: { name, onChange, onBlur, value } }) => (
										<TextField
											className={classes.formInputText}
											select={item.type === 'select' ? true : false}
											label={
												<div>
													{t(item.name)}
													{item.rules.required && <span className="required"> * {t('required')}</span>}
												</div>
											}
											name={name}
											id={name}
											variant="outlined"
											size="small"
											InputLabelProps={{
												shrink: true,
											}}
											error={!!errors[name]}
											// defaultValue={formData[name]}
											helperText={t(errors[name]?.message)}
											onChange={onChange}
											onBlur={onBlur}
											value={value}
											disabled={editMode && item.rules.reedit === false}
											InputProps={item.code === 'aircraft_name_external' ? {
												endAdornment: <InputAdornment position="end">
													<Tooltip title={t('AIRCRAFT_NAME_EXTERNAL_DESCRIBE')}>
														<WarningIcon className={clsx(classes.warningIcon)} viewBox="0 0 24 24" alt="Describe" />
													</Tooltip>
												</InputAdornment>
											} : {}}
										>
											{item.type === 'select' && icons.map(({ iconId, icon }) => (
												<MenuItem key={`menu-${iconId}`} value={iconId === 0 && ![6, 7, 8].includes(value) ? value : iconId}>
													<div className={classes.iconBox}>
														{cloneElement(icon, { className: iconId === 0 ? classes.circle : classes.aircraftIcon })}
													</div>
												</MenuItem>
											))}
										</TextField>
									)}
									name={item.code}
								/>
							))}
							<FormControl className={classes.formInputText}>
								<FormLabel className={classes.dragImageBoxLabel}>{t('Image')}</FormLabel>
								<Box
									className={clsx(classes.dragImageBox, onTarget && classes.dragImageBoxActive)}
									onDrop={handleDrop}
									onDragOver={handleDragOver}
									onDragLeave={handleDragLeave}
								>
									<label htmlFor="upload-image" style={{ display: 'flex', flexDirection: 'column' }}>
										{!preview && !formData['aircraft_image'] ?
											<pre className={classes.dragImageTextBox}>
												{t('DROP_IMAGE_DESCRIBE')}
											</pre> :
											<Box className={classes.dragImagePreview}>
												<img src={preview || formData['aircraft_image']} alt="preview" ref={imageRef} />
											</Box>
										}
										<input
											accept="image/*"
											className={classes.input}
											id="upload-image"
											name="image"
											multiple
											type="file"
											onChange={handleImageChange}
											style={{ display: 'none' }}
										/>
									</label>
								</Box>
							</FormControl>
							<FormControl
								className={clsx(classes.formInputText, classes.externalSourceBox)}
								variant="outlined"
								component="fieldset"
							>
								<FormLabel
									component="legend"
									className={classes.externalSourceBoxLabel}
								>
									{t('External Source')}
								</FormLabel>
								<FormGroup row style={{ width: 260 }}>
									{linkableSourceDetailList?.map(source => (
										<Controller
											key={`linked_data_source.${source.data_source_id}`}
											control={control}
											render={({ field: { name, onChange, value } }) => (
												<FormControlLabel
													key={`source-${source.data_source_id}`}
													control={
														<Checkbox
															checked={value}
															onChange={onChange}
															// name={`source-${source.data_source_id}`}
															name={name}
															color="primary"
															size="small"
															disabled={isDisabled(source)}
															style={{ padding: 5 }}
														/>
													}
													label={source.data_source_name}
													style={{ marginRight: 25 }}
												/>
											)}
											name={`linked_data_source.${source.data_source_id}`}
										/>
									))}
								</FormGroup>
							</FormControl>
						</Box>
					</Box>
				</DialogContent>
			</Dialog>
			<Dialog open={openConfirm} onClose={handleCloseConfirm} onKeyUp={handleKeyUpConfirm}>
				<DialogContent>
					<DialogContentText className={classes.newline}>{t('AIRCRAFT_SAVE_CONFIRM')}</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button onClick={handleCloseConfirm} color="primary">Cancel</Button>
					<Button onClick={handleSaveOk} color="primary">Ok</Button>
				</DialogActions>
			</Dialog>
		</Box>
	);
}));

export default AircraftList;
